/**
 * Holds a restored window scroll position while the page layout settles (images, hydrated
 * widgets), like Inertia putting the visitor back at the saved position after a back, a
 * forward or a reload: without it the position is clamped by a document not tall enough
 * yet, or moved by the browser's scroll anchoring. The visitor's own scroll (wheel, touch,
 * key, pointer) ends the hold at once.
 */

/** How long the restored position is held by default. */
export const RESTORE_SETTLE_MS = 3_000;

export interface ScrollHold {
    /** Scrolls back to the position if the hold is still active. */
    apply: () => void;
    /** Ends the hold (no-op once ended). */
    stop: () => void;
}

export function holdScrollPosition(top: number, left = 0, settleMs = RESTORE_SETTLE_MS): ScrollHold {
    const root = document.documentElement;
    const previousAnchor = root.style.overflowAnchor;
    const userScrollEvents = ["wheel", "touchstart", "keydown", "pointerdown"] as const;
    let holding = true;
    const apply = () => {
        if (holding && (Math.round(window.scrollY) !== top || Math.round(window.scrollX) !== left)) {
            window.scrollTo(left, top);
        }
    };
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(apply);
    const stop = () => {
        if (!holding) return;
        holding = false;
        observer?.disconnect();
        root.style.overflowAnchor = previousAnchor;
        for (const event of userScrollEvents) window.removeEventListener(event, stop, true);
    };

    root.style.overflowAnchor = "none";
    for (const event of userScrollEvents) window.addEventListener(event, stop, { capture: true, passive: true });
    apply();
    observer?.observe(document.body);
    window.setTimeout(stop, settleMs);
    return { apply, stop };
}
